import { useCallback, useContext, useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';

import { HOMEPAGE } from '@constants/routes';
import { Requisition } from '@customTypes/requisition';
import { FamilyProfile } from '@customTypes/user';
import { Context } from '@store/store';
import { isFvtOnlyProband } from '@utils/fvtNav';
import { clearUser } from '@utils/openreplay';

import { useKeycloak } from './useKeycloak';

interface IApp {
  campaignCode: string;
  campaignLogoUrl: string;
  external_member_id: string;
  familyProfiles: FamilyProfile[];
  fvtReferralUUID: string | null;
  hasFVTReferral: boolean;
  isFvtOnlyProband: boolean;
  isLoadingUserInfo: boolean;
  isLoggedIn: boolean;
  mostRecentTestRequest: Requisition | null;
  userFirstName: string;
  userLastName: string;
  onInvalidAuth: () => void;
  onLogout: () => void;
  onLogin: () => void;
}

const useApp = (): IApp => {
  const [state] = useContext(Context);
  const { keycloak } = useKeycloak();
  const accessToken = keycloak?.token;

  const fvtReferralUUID = state.fvtReferralUUID;
  const userFirstName = state.userInfo?.name?.first || '';
  const userLastName = state.userInfo?.name?.last || '';

  const isLoggedIn = useMemo(() => !!keycloak?.authenticated && !!accessToken, [keycloak?.authenticated, accessToken]);

  // Loading only while logged in and the profile request has not settled.
  const isLoadingUserInfo = isLoggedIn && !state.hasLoadedUserInfo;

  const onLogout = () => {
    clearUser();
    keycloak?.logout();
  };

  const onInvalidAuth = useCallback(() => {
    clearUser();
    keycloak?.logout({ redirectUri: window.location.href });
  }, [keycloak]);

  const onLogin = useCallback(() => {
    keycloak?.login({ redirectUri: window.location.origin + HOMEPAGE });
  }, [keycloak]);

  // set Sentry user context
  useEffect(() => {
    if (isLoggedIn && state.userId && state.userInfo) {
      Sentry.setUser({
        id: state.userId,
        email: state.userInfo.contact_info?.[0]?.email,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [isLoggedIn, state.userId, state.userInfo]);

  const external_member_id = useMemo(
    () => state.userInfo?.campaign?.external_member_id || '',
    [state.userInfo?.campaign?.external_member_id],
  );
  const campaignCode = useMemo(
    () => state.userInfo?.campaign?.campaign_code || '',
    [state.userInfo?.campaign?.campaign_code],
  );
  const campaignLogoUrl = useMemo(() => state.userInfo?.campaign?.logo_url || '', [state.userInfo?.campaign?.logo_url]);
  const familyProfiles = state.userInfo?.family_profiles ?? [];

  // A guardian reads their wards' requisitions too, so the unfiltered list is not "mine".
  const selfIdentifier = familyProfiles.find((profile: FamilyProfile) => profile.is_self)?.identifier;
  const ownTestRequests: Requisition[] = selfIdentifier
    ? state.testRequests.filter((request: Requisition) => request.individual_id === selfIdentifier)
    : state.testRequests;
  const mostRecentTestRequest = ownTestRequests[0] || null;
  const isFvtOnlyProbandUser = isFvtOnlyProband({
    campaignCode,
    hasFVTReferral: !!fvtReferralUUID,
    hasLoadedTestRequests: !!state.hasLoadedTestRequests,
    ownTestRequestCount: ownTestRequests.length,
  });

  return {
    campaignCode,
    campaignLogoUrl,
    external_member_id,
    familyProfiles,
    fvtReferralUUID,
    hasFVTReferral: !!fvtReferralUUID,
    isFvtOnlyProband: isFvtOnlyProbandUser,
    isLoadingUserInfo,
    isLoggedIn,
    mostRecentTestRequest,
    userFirstName,
    userLastName,
    onLogin,
    onInvalidAuth,
    onLogout,
  };
};

export default useApp;
